// Aba "Modelos" de ComercialPropostas — lista de modelos HTML de proposta + form.
// Extraído de ComercialPropostas.tsx durante refactor topológico.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import IconLixeira from '../../components/IconLixeira';
import RichTextEditor from '../RichTextEditor';
import { Modelo } from './types';
import { getToken, fmtDate } from './utils';
import {
  th, td, btnAcao, btnSec, btnPri, modalOverlay, modalContent,
  labelStyle, inputStyle,
} from './styles';

// ─────────────────────────── MODELOS ───────────────────────────
export function ListaModelos() {
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<Modelo | null>(null);
  const [novo, setNovo] = useState(false);

  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/modelos-proposta`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setModelos(data);
    } finally { setLoading(false); }
  }
  async function excluir(id: string) {
    if (!window.confirm('Desativar este modelo? Não aparecerá mais no menu de geração.')) return;
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/modelos-proposta/${id}`, { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { alert('Erro: ' + e.message); }
  }

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#666' }}>
          Templates HTML com variáveis <code>{'{{cliente.razao_social}}'}</code>, <code>{'{{negocio.titulo}}'}</code>, <code>{'{{composicao.itens_tabela}}'}</code> etc. Reutilizáveis ao gerar uma proposta.
        </div>
        <button onClick={() => setNovo(true)}
          style={{ padding: '6px 16px', fontSize: 12, borderRadius: 5, background: '#0930D0', color: '#fff', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}>
          + Novo modelo
        </button>
      </div>

      <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#020D7F', color: '#fff' }}>
              <th style={th}>Nome</th>
              <th style={th}>Tipo</th>
              <th style={{ ...th, textAlign: 'center', width: 100 }}>Atualizado</th>
              <th style={{ ...th, textAlign: 'center', width: 130 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {modelos.map((m, i) => (
              <tr key={m.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#FAFBFF' }}>
                <td style={{ ...td, fontWeight: 500, color: '#020D7F' }}>{m.nome}</td>
                <td style={{ ...td, color: '#666' }}>{m.tipo || '—'}</td>
                <td style={{ ...td, textAlign: 'center', fontSize: 11, color: '#666' }}>{fmtDate(m.atualizado_em)}</td>
                <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <button onClick={() => setEditando(m)} style={btnAcao('#0930D0')}>Editar</button>
                  <button onClick={() => excluir(m.id)} style={btnAcao('#B91626')}><IconLixeira /></button>
                </td>
              </tr>
            ))}
            {modelos.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: '#aaa' }}>
                Nenhum modelo cadastrado.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editando || novo) && (
        <FormModelo modelo={editando}
          onClose={() => { setEditando(null); setNovo(false); }}
          onSaved={async () => { await carregar(); setEditando(null); setNovo(false); }} />
      )}
    </div>
  );
}

function FormModelo({ modelo, onClose, onSaved }: {
  modelo: Modelo | null; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<any>({
    nome: modelo?.nome || '',
    tipo: modelo?.tipo || 'comercial',
    conteudo: '',
  });
  const [carregandoConteudo, setCarregandoConteudo] = useState(!!modelo);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!modelo) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/comercial/modelos-proposta/${modelo.id}`, { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        setForm((f: any) => ({ ...f, conteudo: data.conteudo || '' }));
      } finally { setCarregandoConteudo(false); }
    })();
  }, [modelo]);

  async function salvar() {
    if (!form.nome.trim() || !form.conteudo.trim()) { alert('Nome e conteúdo são obrigatórios'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      if (modelo) {
        await axios.put(`${API_URL}/comercial/modelos-proposta/${modelo.id}`, form, { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/comercial/modelos-proposta`, form, { headers: { Authorization: token } });
      }
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, maxWidth: 920 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#020D7F' }}>
          {modelo ? 'Editar modelo' : 'Novo modelo de proposta'}
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Nome *</label>
            <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex.: Topografia — proposta padrão" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Tipo</label>
            <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} style={inputStyle}>
              <option value="comercial">Comercial</option>
              <option value="tecnica">Técnica</option>
              <option value="art">ART</option>
              <option value="outros">Outros</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle}>Conteúdo *</label>
          {carregandoConteudo ? (
            <div style={{ padding: 30, color: '#888', textAlign: 'center' }}>Carregando conteúdo...</div>
          ) : (
            <RichTextEditor
              value={form.conteudo}
              onChange={(html) => setForm({ ...form, conteudo: html })}
              mostrarVariaveis
              height={420}
            />
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
          <button onClick={onClose} style={btnSec}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={btnPri}>
            {salvando ? 'Salvando...' : 'Salvar modelo'}
          </button>
        </div>
      </div>
    </div>
  );
}
